import React, { useState } from 'react';
import { PlacesType, Tooltip } from 'react-tooltip';
import { Icon, IconKey } from '/imports/ui/Icon';
import { defer, randomString } from '/imports/utils';

export interface InputProps<T = string> {
  id?: string;
  inputId?: string;
  className?: string;
  type?: 'text' | 'password' | 'number' | 'color';
  placeholder?: string;
  value?: T;
  disabled?: boolean;
  autoFocus?: boolean;
  onChange?(value: T | undefined, e?: React.ChangeEvent<HTMLInputElement>): void;
  onFocus?(e: React.FocusEvent<HTMLInputElement, Element>): void;
  onBlur?(e: React.FocusEvent<HTMLInputElement, Element>): void;
  onClick?(e: React.MouseEvent<HTMLInputElement, MouseEvent>): void;
  onKeyPress?(e: React.KeyboardEvent<HTMLInputElement>): void;
  onEnter?(value: T | undefined, e?: React.KeyboardEvent<HTMLInputElement>): void;
  onClear?(): void;
  autoComplete?: 'on' | 'off';
  icon?: IconKey;
  iconPlacement?: 'start' | 'end';
  hasError?: boolean;
  tip?: string;
  tipPlacement?: PlacesType;
  'data-tooltip-id'?: string;
  tipEvents?: {
    mouseenter?: boolean;
    focus?: boolean;
    mouseover?: boolean;
    click?: boolean;
    dblclick?: boolean;
    mousedown?: boolean;
  };
  required?: boolean;
}

// Styled input-box container (the former styled.span). Used directly by
// DateInput/DateTimeInput/PhoneInput, which place their own <input>/icons
// inside it. Descendant utilities mirror the original InputStyled CSS exactly.
const inputStyledClass = [
  'relative',
  '[&_input]:box-border [&_input]:min-h-input [&_input]:rounded-input [&_input]:border [&_input]:border-mid [&_input]:bg-page [&_input]:py-0 [&_input]:pr-10 [&_input]:pl-3 [&_input]:font-sans [&_input]:text-[13px] [&_input]:text-fg-1',
  '[&_input::placeholder]:not-italic [&_input::placeholder]:text-fg-3 [&_input:focus-visible]:border-primary/50 [&_input:focus-visible]:ring-2 [&_input:focus-visible]:ring-primary/50 [&_input:focus-visible]:outline-none [&_input:disabled]:cursor-not-allowed [&_input:disabled]:bg-page-alt [&_input:disabled]:text-fg-3 [&_input.has-start-icon]:pl-10',
  '[&_.clear]:absolute [&_.clear]:top-1/2 [&_.clear]:right-3 [&_.clear]:-translate-y-1/2 [&_.clear]:inline-flex [&_.clear]:w-4 [&_.clear]:cursor-pointer',
  '[&_.icon-start]:absolute [&_.icon-start]:top-1/2 [&_.icon-start]:left-3 [&_.icon-start]:-translate-y-1/2 [&_.icon-start]:text-fg-3',
  '[&_.icon]:absolute [&_.icon]:top-1/2 [&_.icon]:right-3 [&_.icon]:-translate-y-1/2 [&_.icon]:inline-flex [&_.icon]:text-fg-3 [&_.icon]:w-4 [&_.icon]:cursor-pointer [&_.icon.toggle-visibility]:right-12 [&_.icon.toggle-visibility]:w-4',
  '[&.error_input]:border-sell',
].join(' ');

export const InputStyled = (
  { id, className, children }: { id?: string; className?: string; children?: React.ReactNode },
) => (
  <span id={id} className={`${inputStyledClass}${className ? ` ${className}` : ''}`}>{children}</span>
);

export const Input = (props: InputProps) => {
  const [type, setType] = useState(props.type || 'text');
  const [tipId,] = useState(props['data-tooltip-id'] || `tip${randomString()}`);
  const clearInput = () => {
    props.onChange?.('');
    defer(() => props.onClear?.());
  };
  const hasStartIcon = props.icon && props.iconPlacement !== 'end';
  const togglePasswordVisibility = () => {
    setType(type === 'password' ? 'text' : 'password');
  };
  return (
    <span id={props.id} className={`input relative [&.error_input]:border-sell ${props.className || ''}${props.hasError ? ' error' : ''}`}>
      {hasStartIcon && <Icon className="icon-start absolute top-1/2 left-3 -translate-y-1/2 text-fg-3" icon={props.icon as any} />}
      <input
        id={props.inputId}
        className={`box-border min-h-input rounded-input border border-mid bg-page py-0 pr-10 pl-3 font-sans text-[13px] text-fg-1 placeholder:not-italic placeholder:text-fg-3 focus-visible:border-primary/50 focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:outline-none disabled:cursor-not-allowed disabled:bg-page-alt${hasStartIcon ? ' has-start-icon pl-10' : ''}`}
        type={type || props.type || 'text'}
        placeholder={props.placeholder || ''}
        disabled={props.disabled}
        autoFocus={props.autoFocus}
        value={props.value || ''}
        onChange={(e) => props.onChange?.(e.target.value, e)}
        onClick={(e) => props.onClick?.(e)}
        onFocus={props.onFocus}
        onBlur={props.onBlur}
        onKeyUp={(e) => {
          props.onKeyPress?.(e);
          if (e.key === 'Enter') {
            defer(() => props.onEnter?.(props.value, e));
          }
        }}
        autoComplete={props.autoComplete}
        data-tooltip-id={tipId}
        required={props.required}
      />
      {
        props.value?.length &&
        !props.disabled &&
        !(props.type === 'color') &&
        <Icon className="clear absolute top-0 right-5 inline-flex h-full w-4 cursor-pointer" icon="cancel" onClick={() => clearInput()} /> || null
      }
      {props.value?.length && props.type === 'password' && <Icon className="toggle-visibility absolute top-0 right-12 inline-flex h-full w-4 cursor-pointer" icon={type === 'password' ? 'visibilityOff' : 'visibility'} onClick={() => togglePasswordVisibility()} /> || null}
      {
        props.tip ?
          <Tooltip id={tipId} content={props.tip} place={props.tipPlacement} openEvents={props.tipEvents || { mouseover: true }} />
          :
          null
      }
    </span>
  );
};